// ==================== CUSTOMER SETTINGS LAYOUT ====================

import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  User, 
  CreditCard, 
  Receipt, 
  Wallet,
  ChevronRight,
  Settings as SettingsIcon,
  Smartphone,
  MessageSquare,
  Settings2,
  Building2,
} from 'lucide-react';
import { ROUTES } from '../../../constants/routes.constants';

const settingsNav = [
  {
    label: 'Profile',
    href: ROUTES.CUSTOMER.SETTINGS_PROFILE,
    icon: User,
    description: 'Manage your personal information',
  },
  {
    label: 'Organization',
    href: ROUTES.CUSTOMER.SETTINGS_ORGANIZATION,
    icon: Building2,
    description: 'Manage business information',
  },
  {
    label: 'WhatsApp Integration',
    href: ROUTES.CUSTOMER.SETTINGS_WHATSAPP,
    icon: Smartphone,
    description: 'Configure WhatsApp & Meta API',
  },
  {
    label: 'SMS Integration',
    href: ROUTES.CUSTOMER.SETTINGS_SMS,
    icon: MessageSquare,
    description: 'Configure SMS provider API',
  },
  {
    label: 'Custom Fields',
    href: ROUTES.CUSTOMER.SETTINGS_CUSTOM_FIELDS,
    icon: Settings2,
    description: 'Manage contact custom fields',
  },
  {
    label: 'Billing & Subscription',
    href: ROUTES.CUSTOMER.SETTINGS_BILLING,
    icon: CreditCard,
    description: 'View and manage your subscription',
  },
  {
    label: 'Payment Methods',
    href: ROUTES.CUSTOMER.SETTINGS_PAYMENT_METHODS,
    icon: Wallet,
    description: 'Manage your payment methods',
  },
  {
    label: 'Payment History',
    href: ROUTES.CUSTOMER.SETTINGS_PAYMENT_HISTORY,
    icon: Receipt,
    description: 'View invoices and payment history',
  },
];

const SettingsLayout = () => {
  const location = useLocation();

  return (
    <div className="p-4 lg:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500/20 to-orange-600/10 flex items-center justify-center">
            <SettingsIcon className="w-4 h-4 text-orange-500" />
          </div>
          <div>
            <h1 className="text-lg font-heading font-semibold text-white">Settings</h1>
            <p className="text-xs text-(--color-muted-gray)">Manage your account preferences</p>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar Navigation */}
        <motion.aside 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="lg:w-64 shrink-0"
        >
          <nav className="space-y-1">
            {settingsNav.map((item) => {
              const isActive = location.pathname === item.href;
              const Icon = item.icon;
              
              return (
                <NavLink
                  key={item.href}
                  to={item.href}
                  className={`
                    group flex items-center gap-2.5 px-3 py-2.5 rounded-lg transition-all duration-200
                    ${isActive 
                      ? 'bg-gradient-to-r from-orange-500/15 to-orange-600/5 border border-orange-500/30' 
                      : 'hover:bg-[#1A2332] border border-transparent hover:border-gray-800'
                    }
                  `}
                >
                  <div className={`
                    w-8 h-8 rounded-md flex items-center justify-center transition-colors
                    ${isActive 
                      ? 'bg-orange-500/20 text-orange-400' 
                      : 'bg-[#1A2332] text-gray-400 group-hover:text-gray-300'
                    }
                  `}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${isActive ? 'text-orange-400' : 'text-gray-200'}`}>
                      {item.label}
                    </p>
                    <p className="text-[11px] text-gray-500 truncate">
                      {item.description}
                    </p>
                  </div>
                  <ChevronRight className={`
                    w-3.5 h-3.5 transition-transform
                    ${isActive 
                      ? 'text-orange-400 translate-x-1' 
                      : 'text-gray-600 group-hover:translate-x-1'
                    }
                  `} />
                </NavLink>
              );
            })}
          </nav>
        </motion.aside>

        {/* Main Content */}
        <motion.main 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex-1 min-w-0"
        >
          <Outlet />
        </motion.main>
      </div>
    </div>
  );
};

export default SettingsLayout;

